import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import api, { API } from "@/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Plus, Search, Trash2, Edit3, ScanLine, Printer, Camera } from "lucide-react";
import { toast } from "sonner";
import BarcodeScanner from "@/components/BarcodeScanner";
import useRevalidate from "@/lib/useRevalidate";
import { Link } from "react-router-dom";

const UNITS = ["kg", "gm", "L", "ml", "pcs", "pack", "dozen", "bag", "box", "bundle"];

const empty = { name_en: "", name_gu: "", unit: "pcs", cost_price: 0, selling_price: 0, stock: 0, barcode: "" };

export default function Items() {
  const { t, user } = useApp();
  const isOwner = user?.role === "owner";
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [delId, setDelId] = useState(null);
  const [scanOpen, setScanOpen] = useState(false);
  const [labelId, setLabelId] = useState(null);
  const [labelCount, setLabelCount] = useState(24);

  const load = () => api.get("/items", { params: q ? { q } : {} }).then((r) => setItems(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q]);
  useRevalidate(load);

  const save = async () => {
    // At least one of the two names is required. English name is optional per product spec.
    if (!(form.name_en || "").trim() && !(form.name_gu || "").trim()) {
      return toast.error(t("name_required"));
    }
    try {
      if (editId) await api.put(`/items/${editId}`, form);
      else await api.post("/items", form);
      toast.success(t("item_saved"));
      setOpen(false); setForm(empty); setEditId(null); load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };

  const doDel = async () => {
    const id = delId;
    try {
      await api.delete(`/items/${id}`);
      // Optimistic UI update — avoids stale HTTP-cache refetch
      setItems((prev) => prev.filter((it) => it.id !== id));
      toast.success("Deleted");
      setDelId(null);
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); setDelId(null); }
  };

  const openEdit = (it) => { setForm({ ...it }); setEditId(it.id); setOpen(true); };
  const openNew = () => { setForm(empty); setEditId(null); setOpen(true); };

  const printLabels = async () => {
    if (!labelId) return;
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${API}/items/${labelId}/labels?count=${labelCount}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const w = window.open(url, "_blank");
      // Best-effort auto print prompt
      setTimeout(() => { try { w?.print(); } catch (e) {} }, 800);
      setLabelId(null);
    } catch (err) { toast.error("Print failed"); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="font-heading text-3xl font-bold text-stone-900">{t("items")}</h1>
        <div className="flex gap-2">
          {isOwner && (
            <Link to="/items/bulk-photo" data-testid="bulk-from-photo-btn">
              <Button variant="outline" className="h-12 rounded-xl border-stone-300 font-medium">
                <Camera className="w-4 h-4 mr-2" /> {t("bulk_from_photo")}
              </Button>
            </Link>
          )}
          {isOwner && (
            <Button onClick={openNew} data-testid="add-item-btn" className="h-12 rounded-xl bg-[#B45309] hover:bg-[#92400E] text-white font-semibold px-5">
              <Plus className="w-4 h-4 mr-2" /> {t("add_item")}
            </Button>
          )}
        </div>
      </div>
      <div className="relative">
        <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} data-testid="items-search" placeholder={t("search")} className="pl-11 h-12 rounded-xl bg-stone-100 border-transparent" />
      </div>

      {items.length === 0 && <Card className="p-10 text-center text-stone-500" data-testid="empty-items">{t("empty_items")}</Card>}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((it) => (
          <Card key={it.id} className="p-4 rounded-2xl border-stone-200" data-testid={`item-card-${it.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-heading font-semibold text-stone-900 truncate">{it.name_en || it.name_gu || "—"}</div>
                <div className="font-gujarati text-sm text-stone-600 truncate">{it.name_en ? it.name_gu : ""}</div>
              </div>
              <div className="flex gap-1">
                <button onClick={() => setLabelId(it.id)} data-testid={`print-label-${it.id}`} className="h-9 w-9 rounded-lg bg-stone-100 grid place-items-center text-stone-700" title={t("print_labels")}><Printer className="w-4 h-4" /></button>
                {isOwner && <button onClick={() => openEdit(it)} data-testid={`edit-item-${it.id}`} className="h-9 w-9 rounded-lg bg-stone-100 grid place-items-center text-stone-700"><Edit3 className="w-4 h-4" /></button>}
                {isOwner && <button onClick={() => setDelId(it.id)} data-testid={`del-item-${it.id}`} className="h-9 w-9 rounded-lg bg-red-50 grid place-items-center text-red-600"><Trash2 className="w-4 h-4" /></button>}
              </div>
            </div>
            <div className={`mt-3 grid gap-2 text-center ${isOwner ? "grid-cols-3" : "grid-cols-2"}`}>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{t("selling_price")}</div>
                <div className="font-mono font-semibold text-stone-900">₹{it.selling_price}</div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{t("stock")}</div>
                <div className={`font-mono font-semibold ${it.stock < 5 ? "text-red-600" : "text-stone-900"}`}>{it.stock} {it.unit}</div>
              </div>
              {isOwner && (
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500">{t("cost_price")}</div>
                  <div className="font-mono font-semibold text-stone-900">₹{it.cost_price}</div>
                </div>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editId ? t("edit") : t("add_item")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">
                {t("name_gu")} <span className="text-red-600 normal-case font-bold">*</span>
              </label>
              <Input placeholder={t("name_gu")} value={form.name_gu} onChange={(e) => setForm({ ...form, name_gu: e.target.value })} data-testid="item-name-gu" className="h-12 rounded-lg font-gujarati" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">
                {t("name_en")} <span className="text-stone-400 normal-case">({t("optional")})</span>
              </label>
              <Input placeholder={t("name_en")} value={form.name_en} onChange={(e) => setForm({ ...form, name_en: e.target.value })} data-testid="item-name-en" className="h-12 rounded-lg" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">
                  {t("unit")}
                </label>
                <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} data-testid="item-unit" className="h-12 w-full rounded-lg border border-stone-300 px-3 bg-white">
                  {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">
                  {t("stock")} <span className="text-stone-400 normal-case">({t("optional")})</span>
                </label>
                <Input type="number" placeholder="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: parseFloat(e.target.value) || 0 })} data-testid="item-stock" className="h-12 rounded-lg font-mono" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              {isOwner && (
                <div>
                  <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">
                    {t("cost_price")} <span className="text-stone-400 normal-case">({t("optional")})</span>
                  </label>
                  <Input type="number" placeholder="0" value={form.cost_price} onChange={(e) => setForm({ ...form, cost_price: parseFloat(e.target.value) || 0 })} data-testid="item-cost" className="h-12 rounded-lg font-mono" />
                </div>
              )}
              <div>
                <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">
                  {t("selling_price")} <span className="text-red-600 normal-case font-bold">*</span>
                </label>
                <Input type="number" placeholder="0" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: parseFloat(e.target.value) || 0 })} data-testid="item-price" className="h-12 rounded-lg font-mono" />
              </div>
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-widest text-stone-500 block mb-1">
                {t("barcode")} <span className="text-stone-400 normal-case">({t("optional")})</span>
              </label>
              <div className="flex gap-2">
                <Input placeholder={t("barcode")} value={form.barcode} onChange={(e) => setForm({ ...form, barcode: e.target.value })} data-testid="item-barcode" className="h-12 rounded-lg font-mono" />
                <button type="button" onClick={() => setScanOpen(true)} data-testid="item-scan-btn"
                        className="h-12 w-12 rounded-lg bg-stone-100 grid place-items-center text-stone-700 shrink-0">
                  <ScanLine className="w-5 h-5" />
                </button>
              </div>
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setOpen(false)} data-testid="item-cancel">{t("cancel")}</Button>
            <Button onClick={save} data-testid="item-save" className="bg-[#B45309] hover:bg-[#92400E] text-white">{t("save")}</Button>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delId} onOpenChange={(o) => !o && setDelId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_delete")}</AlertDialogTitle>
            <AlertDialogDescription>{t("confirm_delete")}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="del-cancel">{t("cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={doDel} data-testid="del-confirm" className="bg-red-600 hover:bg-red-700">{t("yes_delete")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BarcodeScanner open={scanOpen} onOpenChange={setScanOpen} onDetected={(code) => setForm({ ...form, barcode: code })} />

      <Dialog open={!!labelId} onOpenChange={(o) => !o && setLabelId(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>{t("print_labels")}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <label className="block text-xs font-bold uppercase tracking-widest text-stone-500">{t("labels_count")}</label>
            <Input type="number" min={1} max={240} value={labelCount} onChange={(e) => setLabelCount(parseInt(e.target.value) || 1)} data-testid="labels-count" className="h-14 text-2xl font-mono text-center rounded-lg" />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setLabelId(null)}>{t("cancel")}</Button>
              <Button onClick={printLabels} data-testid="labels-print-submit" className="bg-[#B45309] hover:bg-[#92400E] text-white">
                <Printer className="w-4 h-4 mr-2" /> {t("print")}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
